import { useContext, useEffect, useState } from 'react'
import { ThemeContext } from '../App'
import { IChartConfig } from '../components/Chart/interface'

type ConfigBuilder = (color: string, inverseColor: string, timeframe: string) => Promise<IChartConfig>

interface UseChartDataResult {
	dataSeries: IChartConfig | undefined
	timeframe: string
	setTimeframe: React.Dispatch<React.SetStateAction<string>>
	isLoading: boolean
	error: string | null
	retry: () => void
}

export function useChartData(configBuilder: ConfigBuilder, defaultTimeframe: string = '1d'): UseChartDataResult {
	const { theme } = useContext(ThemeContext)
	const [dataSeries, setDataSeries] = useState<IChartConfig | undefined>(undefined)
	const [timeframe, setTimeframe] = useState(defaultTimeframe)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [retryCount, setRetryCount] = useState(0)

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			setIsLoading(true)
			setError(null)

			const mainColor = theme === 'dark' ? 'white' : 'black'
			const inverseMainColor = theme === 'dark' ? 'black' : 'white'

			try {
				const result = await configBuilder(mainColor, inverseMainColor, timeframe)
				if (!cancelled) {
					setDataSeries(result)
				}
			} catch (err: any) {
				if (!cancelled) {
					console.error(err)
					setError(err?.message || 'Failed to load chart data')
					setDataSeries(undefined)
				}
			} finally {
				if (!cancelled) {
					setIsLoading(false)
				}
			}
		}

		load()

		return () => {
			cancelled = true
		}
	}, [theme, timeframe, configBuilder, retryCount])

	const retry = () => setRetryCount((c) => c + 1)

	return { dataSeries, timeframe, setTimeframe, isLoading, error, retry }
}
